import React, { useEffect, useRef, useState } from 'react'
import { message, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import style from './style/myUploadFileStyle.module.scss'
import { CloudDownloadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import FileSaver from 'file-saver'

interface Props {
  multiple?: boolean | undefined
  preview?: boolean | undefined
  remove?: boolean | undefined
  download?: boolean | undefined
  disabled?: boolean | undefined
  onChange?: any
  onRemove?: any
  maxFiles?: number
  minFiles?: number
  dontFixLimitDelete?: boolean
  defaultData?: any[]
}

const MyUploadFile: React.FC<Props> = ({
  multiple,
  preview,
  remove = true,
  download = true,
  disabled,
  maxFiles,
  minFiles,
  defaultData,
  dontFixLimitDelete = true,
  onChange: callbackImages,
  onRemove: callbackRemove,
}) => {
  const { t } = useTranslation()
  const [arrFiles, setArrFiles] = useState<any>([])
  const [imagePreviews, setImagePreviews] = useState<any | undefined>([])
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewTitle, setPreviewTitle] = useState('')
  const [previewImage, setPreviewImage] = useState('')
  const [maxFileDefault, setMaxFileDefault] = useState<any>(0)
  const [visibleAddPhoto, setVisibleAddPhoto] = useState<boolean | undefined>(true)
  const upload: React.RefObject<any> = useRef(null)

  useEffect(() => {
    maxFiles && setMaxFileDefault(maxFiles)
  }, [maxFiles])

  useEffect(() => {
    setVisibleAddPhoto(!disabled)
  }, [disabled])

  useEffect(() => {
    if (defaultData && defaultData?.length > 0) {
      if (defaultData.length >= 3) setVisibleAddPhoto(false)
      const newArr = defaultData?.map(({ url }: { url: string }) => url)

      setImagePreviews(newArr)
      setArrFiles(defaultData)
    }

    return () => {
      setImagePreviews(null)

      setArrFiles(null)
    }
  }, [defaultData])

  const handleUpload = (e: any) => {
    let files = e?.target?.files

    if (arrFiles?.length > 0) {
      files = [...arrFiles, ...files]
    }

    if (maxFileDefault > 0 && files.length > maxFileDefault) {
      message.error(`${t('h144')}: ${maxFileDefault}`)
    }

    const filesArray = Array.from(files)
    const slicedFiles = maxFileDefault ? filesArray.slice(0, maxFileDefault) : filesArray

    const previews: any[] = Array.from(slicedFiles).map((file: any) => {
      if (file instanceof File) {
        return URL.createObjectURL(file)
      } else {
        return file.url
      }
    })

    if (slicedFiles.length >= 3) setVisibleAddPhoto(false)

    setArrFiles(slicedFiles)
    setImagePreviews(previews)
    callbackImages(slicedFiles)

    upload.current.value = ''
  }

  const handleDelete = (indexToRemove: number) => {
    if (dontFixLimitDelete) {
      if (imagePreviews?.length <= 1 && arrFiles?.length <= 1) {
        message.error(`${t('h151')}: ${minFiles}`)
        return false
      }
    }

    const updatedPreviews = filterEl(imagePreviews, indexToRemove, 'note')
    const updatedArrFiles = filterEl(arrFiles, indexToRemove, 'note')

    setVisibleAddPhoto(checkVisibleAddPhoto(updatedArrFiles))
    setImagePreviews(updatedPreviews)
    setArrFiles(updatedArrFiles)

    callbackImages(updatedArrFiles)
    callbackRemove(arrFiles[indexToRemove])
  }

  const handleDownload = (index: number) => {
    let urlFile: string
    const file = filterEl(arrFiles, index, '')
    if (file instanceof File) {
      urlFile = URL.createObjectURL(file)
    } else {
      urlFile = file.url
    }

    FileSaver.saveAs(urlFile, `1231`)
  }

  const handlePreview = async (indexToPreview: number) => {
    const file: any = filterEl(arrFiles, indexToPreview, '')
    let urlFile: string
    let urlName: string

    if (file instanceof File) {
      urlFile = URL.createObjectURL(file)
      urlName = file.name
    } else {
      urlFile = file.url
      urlName = file.url
    }

    setPreviewImage(urlFile)
    setPreviewTitle(urlName)
    setPreviewOpen(true)
  }

  const handleCancelPreview = () => setPreviewOpen(false)

  const filterEl = (data: any, indexToRemove: number, type: string) => {
    if (type === 'note') {
      return data.filter((el: any, index: number) => index !== indexToRemove)
    } else {
      return data.filter((el: any, index: number) => index === indexToRemove)[0]
    }
  }

  const checkVisibleAddPhoto = (data: any[]) => {
    return data.length < maxFileDefault
  }

  return (
    <>
      <div className={`${style.wrapper_upload_file} ${(preview || remove || download) && style.preview}`}>
        <div className={style.wrapper_items}>
          {imagePreviews?.map((url: any, index: number) => {
            return (
              <div key={index} className={style.item}>
                <div className={style.wrapper_img}>
                  <div className={style.img}>
                    <img src={url} alt={`preview-${index}`} />
                    <div className={style.wrapper_icon}>
                      {preview && (
                        <div className={style.icon} onClick={() => handlePreview(index)}>
                          <EyeOutlined />
                        </div>
                      )}
                      {download && (
                        <div className={style.icon} onClick={() => handleDownload(index)}>
                          <CloudDownloadOutlined />
                        </div>
                      )}
                      {!disabled && remove && (
                        <div className={`${style.icon} ${style.delete}`} onClick={() => handleDelete(index)}>
                          <DeleteOutlined />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {visibleAddPhoto && (
          <div className={style.wrapper_upload}>
            <div className={style.upload} onClick={() => upload && upload.current.click()}>
              <input
                disabled={disabled}
                ref={upload}
                type="file"
                onChange={handleUpload}
                multiple={multiple}
                accept="image/png, image/jpeg, image/jpg"
              />
              <span>+ Загрузка</span>
            </div>
          </div>
        )}
      </div>
      <Modal
        open={previewOpen}
        title={previewTitle}
        className="fz_12_title"
        footer={null}
        onCancel={handleCancelPreview}
      >
        <img alt="example" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </>
  )
}

export default MyUploadFile
